import React from 'react';

import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { Link } from 'react-router-dom';

import { FORMS } from '../../schemas/stepMap';
import { useSteps } from '../../hooks/useSteps';
import BackForwardButtons from '../../components/buttons/BackForwardButtons';
import FormBuilder from '../../components/formBuilder';
import RehabKeyMetrics from './RehabKeyMetrics';
import RehabProjectionsSummary from './RehabProjectionsSummary';
import RehabSummaryOverview from './RehabSummaryOverview';

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import toast from 'react-hot-toast';
import getCurrentDateFormatted from '../../utility/dates';
import { Logo } from '../../components';
import formatAddress from '../../utility/formatAddress';

export default function PurchaseSummaryView() {
  const { prev, next } = useSteps();
  const { pathname } = useLocation();

  const BuyAndHoldForm = FORMS.BuyAndHoldAnalysisSimple;
  const BuyAndHoldSchema = BuyAndHoldForm.schema;

  const { propertyData, scenarios } = useOutletContext();
  const scenarioData = scenarios.find((scenario) => !scenario.isDefault);

  if (!scenarioData?.rehabWorksheetSubmitted) {
    return (
      <div className="w-full flex flex-col items-center justify-center mt-36">
        <h2 className="mb-6 text-xl font-medium text-primary">
          No Buy and Hold Analysis Created
        </h2>
        {!pathname.includes('sharing') && (
          <p className="mb-72 w-2/3 text-center">
            Once you have filled out the{' '}
            <Link
              to="../../purchase-worksheet"
              className="font-medium text-primary hover:text-primary-700 focus:text-primary-700"
            >
              purchase worksheet{' '}
            </Link>
            and the{' '}
            <Link
              to="../../rehab-worksheet"
              className="font-medium text-primary hover:text-primary-700 focus:text-primary-700"
            >
              rehab worksheet
            </Link>{' '}
            you will be able to see your buy and hold analysis here.
          </p>
        )}
      </div>
    );
  }

  function onReportClick(some) {
    generateCashflowReport();
  }

  function generateCashflowReport() {
    const element = document.getElementById('cash-flow-report');

    if (!element) {
      return;
    }

    try {
      const clonedElement = element.cloneNode(true);
      clonedElement.style.position = 'absolute';
      clonedElement.style.top = '-9999px';
      clonedElement.style.left = '-9999px';
      clonedElement.classList.add('printing');
      clonedElement.classList.add('p-8');
      clonedElement.style.width = '1080px';
      clonedElement.classList.remove('hidden');
      const h = document.getElementById('cashflow-printable-header');

      const header = h.cloneNode(true);

      header.classList.remove('hidden');
      header.classList.add('grid');
      clonedElement.insertBefore(header, clonedElement.firstChild);

      document.body.appendChild(clonedElement);

      html2canvas(clonedElement, {
        scale: 2,
      }).then((canvas) => {
        const componentWidth = clonedElement.offsetWidth;
        const componentHeight = clonedElement.offsetHeight;

        document.body.removeChild(clonedElement);
        const orientation = componentWidth >= componentHeight ? 'l' : 'p';

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation,
          unit: 'px',
        });

        pdf.internal.pageSize.width = componentWidth;
        pdf.internal.pageSize.height = componentHeight;

        pdf.addImage(imgData, 'PNG', 0, 0, componentWidth, componentHeight);

        const fileName = `${
          propertyData.propertyName
        } - Rehab Cash Flow Report - ${getCurrentDateFormatted()}`;

        pdf.save(`${fileName}.pdf`);
        toast.success('PDF downloaded', { position: 'bottom-right' });
        element.style.width = 'auto';
      });
    } catch (error) {
      console.log('Failed to print the PDF', error);
      setPrinting(false);
      element.style.width = 'auto';
      toast.error('Something went wrong', { position: 'bottom-right' });
    }
  }

  // cash flow report
  const cashFlowSchema = FORMS.rehabCashFlow.schema;

  return (
    <>
      <Helmet>
        <title>PropertyCast.io - Buy and Hold Analysis</title>
      </Helmet>

      {/* cash flow report  */}
      <div
        id="cashflow-printable-header"
        className="grid-cols-4 p-4 hidden mb-4"
      >
        <div className="flex items-center justify-start">
          <Logo variant="dark" size="md" />
        </div>
        <div className="col-span-2 flex items-center justify-center">
          <h1 className="text-center text-4xl font-medium font-serif text-neutral-900">
            {propertyData.propertyName} - Rehab Cash Flow Report
          </h1>
        </div>
        <div className="text-right text-md">
          <p>PropertyCast.io</p>
          <p>{formatAddress(propertyData.propertyAddress, 'short')}</p>
          <br />
          <p>{getCurrentDateFormatted()}</p>
        </div>
      </div>
      <div className="hidden bg-primary-50" id="cash-flow-report">
        <FormBuilder
          formSchema={cashFlowSchema.calculations.schema}
          formType={FORMS.rehabCashFlow.formType}
          beforeData={propertyData}
          afterData={scenarioData}
          onReportClick={onReportClick}
        />
      </div>

      <main>
        <div id="page-summary" className="space-y-10 my-8 bg-primary-50">
          <RehabSummaryOverview
            propertyData={{ ...propertyData, ...scenarioData }}
          />

          <RehabKeyMetrics
            propertyData={propertyData}
            scenarioData={scenarioData}
          />

          <FormBuilder
            onReportClick={onReportClick}
            formType={BuyAndHoldForm.formType}
            calculationsSchema={BuyAndHoldSchema}
            beforeData={propertyData}
            afterData={scenarioData}
          />

          <RehabProjectionsSummary
            propertyData={propertyData}
            scenarioData={scenarioData}
          />
        </div>

        <BackForwardButtons prev={prev} next={next} />
      </main>
    </>
  );
}
